import * as _ from 'lodash'
import {DependencyList, EffectCallback, useEffect, useMemo, useRef} from 'react'

import {useOrderProcessFormContext} from './useOrderProcessFormContext'

const useDeepCompareMemoizeDeps = (deps?: DependencyList | undefined) => {
  const depsRef = useRef<DependencyList | undefined>(undefined)

  // Clone deps to remove Proxy wrappers, then deep compare
  if (deps) {
    const clonedDeps = _.cloneDeep(deps)
    if (!_.isEqual(clonedDeps, depsRef.current)) {
      depsRef.current = clonedDeps
    }
  }
  return depsRef.current ?? []
}

/**
 * useEffect with deep comparison of dependencies.
 *
 * Clones deps to remove Proxy wrappers, then uses _.isEqual for comparison.
 */
export const useDeepCompareEffect = (
  effect: EffectCallback,
  deps?: DependencyList
): void => {
  const memoizedDepsRef = useDeepCompareMemoizeDeps(deps)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, memoizedDepsRef)
}

/**
 * useMemo with deep comparison of dependencies.
 *
 * Clones deps to remove Proxy wrappers, then uses _.isEqual for comparison.
 */
export const useDeepCompareMemo = <T>(factory: () => T, deps?: DependencyList | undefined): T => {
  const memoizedDepsRef = useDeepCompareMemoizeDeps(deps)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(factory, memoizedDepsRef)
}

export const useFormEffect = (effect: EffectCallback, deps?: DependencyList) => {
  const {trigger} = useOrderProcessFormContext()
  useDeepCompareEffect(() => {
    effect()
    trigger()
  }, deps)
}
